import {
    Children,
    cloneElement,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useState
} from "react";

// eslint-disable-next-line no-restricted-imports
import { css, keyframes } from "@emotion/react";
import { useIntl } from "react-intl";

import HipchatChevronDownIcon from "@atlaskit/icon/glyph/hipchat/chevron-down";
import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";
import {
    Logo,
    LogoBrand,
    ProductKeyTypes
} from "../../../../libraries/wpl/foundations/src/index";
import Button, { ButtonTypes } from "../../button/src";

export interface ProductNavBarProps {
    productHref: string; // link for logo
    productName: LogoBrand;
    showFreeHrefButton?: boolean;
    freeHref?: string; // link for Get it free button
    ariaLabel?: string;
    dataProductKey: ProductKeyTypes; // product key label for Get it free button
    logoLinkWrapperWidth?: string;
}

const navigationWrapperCss = css`
    position: sticky;
    top: 0;
    z-index: 3;
`;

const navigationBarWrapperCss = css`
    align-items: flex-start;
    background: #ffffff;
    display: flex;
    flex-direction: row;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
`;

const navigationBarCss = css`
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: space-between;

    ${media.above.md} {
        height: 60px;
        max-width: 1200px;
        padding: 0 ${token("space.200")};
    }

    ${UNSAFE_media.below.md} {
        height: 56px;
        padding: 0 5px 0 ${token("space.200")};
        position: relative;
        z-index: 1;
    }

    ${UNSAFE_media.below.sm} {
        flex-flow: row wrap;
        height: auto;
        padding: 0 ${token("space.200")};
    }
`;

const logoAndLinksCss = css`
    display: flex;
`;

const logoWrapperCss = css`
    align-items: center;
    display: flex;
`;

const categoriesWrapperCss = css`
    display: flex;
    flex: 1;
    flex-direction: row;
    list-style: none;
    margin: 0 ${token("space.200")};
    padding: 0;

    ${UNSAFE_media.below.md} {
        display: none;
    }
`;

const keyframeChevron = keyframes`
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(-180deg);
    }
`;

const removeKeyframeChevron = keyframes`
    0% {
        transform: rotate(-180deg);
    }

    100% {
        transform: rotate(0deg);
    }
`;

const dropdownCss = css`
    display: none;
    cursor: pointer;

    ${UNSAFE_media.below.md} {
        background-color: rgba(245, 40, 145, 0);
        border: none;
        color: #0052cc;
        display: block;
        padding-top: ${token("space.075")};

        svg {
            height: ${token("space.400")};
            width: ${token("space.400")};
        }
    }
`;

const animateDropdownArrowCss = css`
    svg {
        animation: ${keyframeChevron} 300ms ease;
        animation-fill-mode: forwards;
    }
`;

const removeAnimateDropdownArrowCss = css`
    svg {
        animation: ${removeKeyframeChevron} 300ms ease;
        animation-fill-mode: forwards;
    }
`;

const dropdownSlideInAnimation = keyframes`
    from {
        max-height: 0px;
    }

    to {
        max-height: 500px;
    }
`;

const dropdownSlideOutAnimation = keyframes`
    from {
        max-height: 500px;
    }

    to {
        max-height: 0px;
        display: none;
    }
`;

const mobileCategoriesWrapperCss = css`
    flex: 1;
    border-color: rgb(9, 30, 66);
    0px -4px 5px rgba(0, 0, 0, 0.2);
    background-color: #f4f5f7;
    border-top: 1px solid #c1c7d0;
    border-bottom: 1px solid #dfe1e5;
    box-shadow: 0 2px 10px rgba(0, 0, 0, .25);
    max-height: 0px;
    overflow-x: hidden;
    overflow-y: auto;
    position: absolute;
    width: 100%;

    ul {
        margin: 0;
        padding: 15px 0px 15px 30px;
        list-style-type: none;

        ${UNSAFE_media.below.md} {
            padding-right: 15px;
        }
    }

    ${media.above.md} {
        display: none;
    }
`;

const mobileCategoriesWrapperShowCss = css`
    animation: ${dropdownSlideInAnimation} 800ms ease forwards;
`;

const mobileCategoriesWrapperHideCss = css`
    animation: ${dropdownSlideOutAnimation} 600ms ease forwards;
`;

const listItemCss = css`
    position: relative;
`;

const buttonWrapper = css`
    display: flex;
    padding: 15px 0;
`;

const linkAndButtonWrapper = css`
    display: flex;
    align-items: center;
`;

const buttonStyle = css`
    font-size: 1rem;
    font-size: ${token("space.200")};
    font-weight: 500;
    height: 2rem;
    width: auto;
    float: right;
    transition: background 250ms;
    text-wrap: nowrap;

    ${UNSAFE_media.below.sm} {
        position: relative;
        width: auto;
        flex-flow: row wrap;
        right: unset;
    }
`;

export const ProductNavBar = (props: PropsWithChildren<ProductNavBarProps>) => {
    const intl = useIntl();
    const isShowFreeHrefButton = props.showFreeHrefButton ?? true;

    const [activeDropdown, setActive] = useState<boolean | null>(null);

    const addAnimationCss = () => {
        if (activeDropdown === null) return null;

        if (activeDropdown) {
            return animateDropdownArrowCss;
        } else {
            return removeAnimateDropdownArrowCss;
        }
    };

    const [buttonType, setButtonType] = useState<ButtonTypes>("secondary");

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        Initializer.autoTrack();
    }, [activeDropdown, props.showFreeHrefButton]);

    const handleScroll = () => {
        setButtonType(window.scrollY > 56 ? "primary" : "secondary");
    };

    const getItFreeText = intl.formatMessage({
        id: "productNavBar.get-it-free-button",
        defaultMessage: "Get it free"
    });

    const renderSelectionItems = (isMobile: boolean) => {
        return (
            <ul css={isMobile ? null : categoriesWrapperCss}>
                {props.children &&
                    Children.map(props.children, (child, index) =>
                        child ? (
                            <li key={index} css={listItemCss}>
                                {cloneElement(child as ReactElement, {
                                    isMobile
                                })}
                            </li>
                        ) : null
                    )}
            </ul>
        );
    };

    const renderDropdownButton = () => {
        if (props.children) {
            return (
                <button
                    css={[dropdownCss, addAnimationCss()]}
                    onClick={() => setActive(!activeDropdown)}
                >
                    <HipchatChevronDownIcon label="" />
                </button>
            );
        }
        return null;
    };

    const linkWrappedLogoWidth = () =>
        props.logoLinkWrapperWidth ? props.logoLinkWrapperWidth : "157px";

    return (
        <div css={navigationWrapperCss}>
            <div css={navigationBarWrapperCss}>
                <div css={navigationBarCss} className="productNavBar">
                    <div css={logoAndLinksCss}>
                        <a
                            css={logoWrapperCss}
                            style={{ width: linkWrappedLogoWidth() ?? "157px" }}
                            href={props.productHref}
                            aria-label={props.ariaLabel}
                        >
                            <Logo
                                brand={props.productName}
                                type="logo"
                                appearance="brand"
                            />
                        </a>
                        {renderDropdownButton()}
                        {renderSelectionItems(false)}
                    </div>
                    <div css={linkAndButtonWrapper}>
                        {isShowFreeHrefButton && (
                            <div
                                css={buttonWrapper}
                                className="showFreeHrefButton"
                            >
                                <Button
                                    text={getItFreeText}
                                    css={buttonStyle}
                                    url={props.freeHref}
                                    type={buttonType}
                                    aria-label={getItFreeText}
                                    dataEventProps={{
                                        "data-event-container": "navbar",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-label": getItFreeText,
                                        "data-product-key": props.dataProductKey
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                css={[
                    mobileCategoriesWrapperCss,
                    activeDropdown !== null &&
                        (activeDropdown
                            ? mobileCategoriesWrapperShowCss
                            : mobileCategoriesWrapperHideCss)
                ]}
                className="mobileCategoriesWrapper"
            >
                {renderSelectionItems(true)}
            </div>
        </div>
    );
};

export default ProductNavBar;
